'use client';

import { markdownTrilogy } from '@bytel/markdown-trilogy';
import {
    Alert,
    Box,
    BoxContent,
    RadioTiles,
    StatusState,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { ConfigType } from '@app-types/api';
import { PlanType, SimType } from '@app-types/product';
import { TagEventType } from '@app-types/tracking';

import { Cms } from '@components/cms';
import { ListItemWithPrice } from '@components/list-item-with-price';

import { useCart } from '@hooks/use-cart';
import useTracking from '@hooks/use-tracking';

import { ItemBox } from './item-box';

type SimsConfProps = {
    product: PlanType;
    sim?: SimType;
    compatibleSims: SimType[];
    config?: ConfigType;
    onShowEsimModal: () => void;
    onShowEsimCompatibleDevicesModal: () => void;
};

export function SimsConf({
    product,
    sim,
    compatibleSims,
    config,
    onShowEsimModal,
    onShowEsimCompatibleDevicesModal,
}: Readonly<SimsConfProps>) {
    const { sendTag } = useTracking();
    const { updateCart, removeDeliveryMode } = useCart();

    if (!product || !sim) return null;
    const selected = sim.gencode;

    const hasSingleSim = compatibleSims.length < 2;

    const handleSimUpdate = (sim: SimType) => {
        updateCart({ sim });
        if (sim.esim) {
            removeDeliveryMode();
        }
        sendTag(
            {
                actionClic: 'Clic_selection',
                categorieClic: 'Blocs_SIM',
                nomClic: sim.esim ? 'eSIM' : 'SIM',
            },
            TagEventType.CLICK,
        );
    };

    const isSim = !sim.esim;

    return (
        <>
            {hasSingleSim && (
                <>
                    <Title level={TitleLevels.FIVE}>{isSim ? 'Carte SIM' : 'eSIM'}</Title>
                    <ListItemWithPrice
                        title={isSim ? 'Carte SIM triple découpe' : 'eSIM'}
                        subTitle={<small>{isSim ? 'Une puce physique ' : 'Une puce dématérialisée.'}</small>}
                        amount={sim.price}
                        subTitleClassName='has-text-grey-dark is-size-7'
                        priceLevel={5}
                    />
                </>
            )}
            {!hasSingleSim && (
                <>
                    <Title level={TitleLevels.FIVE} markup={TitleMarkup.H3}>
                        Quel type de SIM&nbsp;?
                    </Title>
                    {config?.checkout_sim_choice_text && (
                        <Box shadowless>
                            <BoxContent backgroundColor={TrilogyColor.MAIN_FADE} className='p-small'>
                                <Cms
                                    content={markdownTrilogy(config.checkout_sim_choice_text)}
                                    as={Text}
                                    {...{ level: TextLevels.TWO }}
                                />
                            </BoxContent>
                        </Box>
                    )}
                    <RadioTiles>
                        {compatibleSims.map((sim) => {
                            return (
                                <ItemBox
                                    key={sim.gencode}
                                    active={sim.gencode === selected}
                                    title={sim.esim ? 'eSIM' : 'Carte SIM'}
                                    price={sim.price}
                                    subTitle={
                                        sim.esim
                                            ? 'Disponible dès la validation de votre commande.'
                                            : 'Livrée chez vous sous 3 jours ouvrés.'
                                    }
                                    onChange={() => handleSimUpdate(sim)}
                                />
                            );
                        })}
                    </RadioTiles>
                    <Alert
                        display={sim.esim}
                        status={StatusState.WARNING}
                        title="Votre téléphone est-il bien compatible avec l'eSIM&nbsp;?"
                        description={
                            <>
                                <Text marginless level={TextLevels.THREE}>
                                    Si votre téléphone n&apos;est pas compatible avec une eSIM, des frais peuvent vous
                                    être facturés pour obtenir une carte SIM classique.
                                </Text>
                                <button
                                    type='button'
                                    className='link button-clear'
                                    onClick={onShowEsimCompatibleDevicesModal}
                                >
                                    Voir les téléphones compatibles
                                </button>
                            </>
                        }
                    />

                    <View className='icon-and-text'>
                        <span className='icon is-large'>
                            <i className='tri-esim' aria-hidden='true'></i>
                        </span>
                        <div>
                            <Text marginless level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                L&apos;eSIM, votre SIM dématérialisée
                            </Text>
                            <Text marginless level={TextLevels.TWO} markup='p'>
                                Plus de carte à insérer. Vous recevez un e-mail pour l&apos;activer.
                            </Text>
                            <button type='button' className='link button-clear' onClick={onShowEsimModal}>
                                Comment ça fonctionne&nbsp;?
                            </button>
                        </div>
                    </View>
                </>
            )}
        </>
    );
}
